import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialog, MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { ConstantsService } from 'app/services/constants-service';
import { CurrencyService } from 'app/shared/services/currency-service';
import { FormErrors} from 'app/shared/validators/generic-validators';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';
import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';

import { BankAccount } from "app/shared/models/bank-account.model";
import { BankInstitution } from "app/shared/models/bank-institution.model";

import { FinanceService } from 'app/finance/finance.service';
import { EntityDetailsService } from 'app/entities/details/entity-details.service';

/**
 * Component for adding/editing a Bank Account.
 */
@Component({
    selector: 'bank-account',
    templateUrl: './bank-account.component.html',
})

export class BankAccountComponent {
    bankAccountForm: FormGroup;
    savingBankAccount: boolean;
    selectedBankAccount: BankAccount;
    accountHolderEntityId: number;
    bankAccountId: number;
    bankInstitutionName: string;
    action: string;

    currencies: ApplicationValue[];
    bankInstitution: BankInstitution;
    bankInstitutions: BankInstitution[];
    filteredBanks: Observable<BankInstitution[]>;

    formErrors = {
        'accountName': '',
        'accountNumber': '',
        'bankInstitution': '',
        'currency': '',
    };

    validationMessages = {
        'accountName': {
            'required': 'Required field value.',
        },
        'accountNumber': {
            'required': 'Required field value.',
        },
        'bankInstitution': {
            'required': 'Required field value.',
        },
        'currency': {
            'required': 'Required.',
        },
    };

    constructor(
        public dialogRef: MdDialogRef<any>,
        private formBuilder: FormBuilder,
        private currencyService: CurrencyService,
        private entityDetailsService: EntityDetailsService,
        private financeService: FinanceService,
        private snackBarService: SnackBarService,
        private trackingService: TrackingService,
        private warmUpService: WarmUpService) { }

    createForm() {
        this.bankAccountForm = this.formBuilder.group({
            accountName: ['', Validators.required],
            accountNumber: ['', Validators.required],
            bankInstitution: ['', Validators.required],
            signatureRights: [],
            currencyId: ['', Validators.required],
        });

        this.bankAccountForm.valueChanges.subscribe(data => this.onValueChanged(data));
        this.onValueChanged();
    }

    callSnackBarService(response: any, successMsg: string, failureMsg: string) {
        this.snackBarService.showStatus(response, successMsg, failureMsg);
    }

    displayFn(bank: BankInstitution): string {
        return bank ? bank.bankName : '';
    }

    private filterBanks(val: string): BankInstitution[] {
        return val ? this.bankInstitutions.filter(s => (s.bankName.toLowerCase().indexOf(val.toLowerCase()) === 0)) : this.bankInstitutions;
    }

    ngOnInit() {
        this.createForm();
        this.setBanksList();
        this.setCurrencies();
        // Use setTimeout to ensure selectedBankAccount is set by parent component
        setTimeout(() => {
            this.updateBankAccountForm();
        }, 0);

        this.accountHolderEntityId = this.entityDetailsService.getEntityIdDetails().entityId
    }

    onSubmit({ value, valid }) {
        const msgSuccess = this.action == 'Add' ? MessageService.ADD_BANK_ACCOUNT_SUCCESS : MessageService.EDIT_BANK_ACCOUNT_SUCCESS;
        const msgFailure = this.action == 'Add' ? MessageService.ADD_BANK_ACCOUNT_FAILURE : MessageService.EDIT_BANK_ACCOUNT_FAILURE;

        const bankAccountModel = new BankAccount();
        bankAccountModel.accountId = this.bankAccountId;
        bankAccountModel.accountName = value.accountName;
        bankAccountModel.accountNumber = value.accountNumber;
        bankAccountModel.bankInstitutionId = value.bankInstitution.bankId;
        bankAccountModel.bankInstitutionName = value.bankInstitution.bankName;
        bankAccountModel.signatureRights = value.signatureRights;
        bankAccountModel.currencyId = value.currencyId;

        bankAccountModel.accountHolderEntityId = this.accountHolderEntityId;

        this.savingBankAccount = true;
      //TODO: remove this after test server check
      console.log('banck account model value: ', bankAccountModel);
        
        // Use POST for creating new accounts, PUT for updating existing accounts
        const saveObservable = this.bankAccountId 
            ? this.financeService.updateBankAccount(this.bankAccountId, bankAccountModel)
            : this.financeService.saveBankAccount(bankAccountModel);
        
        saveObservable.subscribe(response => {
                this.savingBankAccount = false;
                if (response.flagResult) {
                    this.dialogRef.close(response);
                }
                this.trackingService.emitEvent(
                    TrackingConstants.BANK_ACCOUNTS,
                    TrackingConstants.SAVE_BANK_ACCOUNT,
                    TrackingConstants.SUCCESS, 1);
                this.callSnackBarService(response, msgSuccess, msgFailure);
            },
            err => {
                this.savingBankAccount = false;
                this.trackingService.emitEvent(
                    TrackingConstants.BANK_ACCOUNTS,
                    TrackingConstants.SAVE_BANK_ACCOUNT,
                    TrackingConstants.FAILURE, 1);
                this.callSnackBarService(err, msgSuccess, msgFailure);
            });
    }

    onValueChanged(data?: any) {
        if (!this.bankAccountForm) {
            return;
        }
        FormErrors('bankAccountForm', this);
    }

    private setBanksList() {
        // If bank institutions are already provided by parent, use them instead of making API call
        if (this.bankInstitutions && this.bankInstitutions.length > 0) {
            this.initializeFilteredBanks();
            // Update form after banks are loaded to ensure bankInstitution is set correctly
            if (this.selectedBankAccount) {
                this.updateBankAccountForm();
            }
        } else {
            // Only make API call if bank institutions are not provided
            this.financeService.getBankInstitutionsList().subscribe(banks => {
              //TODO: remove this after test server check
              console.log('SET BANK LIST: VALUE: ', banks);
                this.bankInstitutions = banks;
                this.initializeFilteredBanks();
                // Update form after banks are loaded to ensure bankInstitution is set correctly
                if (this.selectedBankAccount) {
                    this.updateBankAccountForm();
                }
            });
        }
    }
    
    private initializeFilteredBanks() {
        this.filteredBanks = this.bankAccountForm.get('bankInstitution').valueChanges
            .startWith(null)
            .map(bankInstitution => bankInstitution && typeof bankInstitution === 'object' ? bankInstitution.bankName : bankInstitution)
            .map(name => name ? this.filterBanks(name) : (this.bankInstitutions ? this.bankInstitutions.slice() : this.bankInstitutions));
    }

    private setCurrencies() {
        const currencyValues = this.warmUpService.getApplicationValue(ApplicationTypeConstants.CURRENCY);
        if (currencyValues instanceof Observable) {
            currencyValues.subscribe(
                response => {
                    this.warmUpService.setApplicationTypeInLS(response);
                    this.currencies = response.values;
                }
            );
        } else {
            this.currencies = currencyValues;
        }
    }

    updateBankAccountForm(): void {
        if (this.selectedBankAccount) {
            // Ensure accountId is set for edit mode
            this.bankAccountId = this.selectedBankAccount.accountId;
            
            // Only update form if it exists
            if (!this.bankAccountForm) {
                return;
            }
            
            for (let inner in this.bankAccountForm.controls) {
                this.bankAccountForm.get(inner).markAsTouched();
                this.bankAccountForm.get(inner).updateValueAndValidity();
            }

            // Find the bank institution if not already set
            if (!this.bankInstitution && this.bankInstitutions && this.bankInstitutions.length > 0) {
                this.bankInstitution = this.bankInstitutions.find(b => b.bankId === this.selectedBankAccount.bankInstitutionId);
            }

            this.bankAccountForm.patchValue({
                accountName: this.selectedBankAccount.accountName,
                accountNumber: this.selectedBankAccount.accountNumber,
                currencyId: this.selectedBankAccount.currencyId,
                signatureRights: this.selectedBankAccount.signatureRights,
                bankInstitution: this.bankInstitution
            });
            
            // Log for debugging
            console.log('Bank account form updated:', {
                accountId: this.bankAccountId,
                accountName: this.selectedBankAccount.accountName,
                accountNumber: this.selectedBankAccount.accountNumber,
                bankInstitution: this.bankInstitution
            });
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/entities/finance/bankaccount/bank-account.component.ts